import React, { useEffect, useState, useMemo } from 'react';
import { useYouTubePlayer, UseYouTubePlayerOptions } from '../hooks/useYouTubePlayer';
import { fetchVideoByUUID, VideoEntry } from '../services/api';

interface YouTubePlayerProps {
  /** UUID del video en la base de datos, o un YouTube video ID directo */
  videoIdentifier: string;
  /** Si true, resuelve el identifier como UUID desde la API */
  resolveFromAPI?: boolean;
  /** Opciones del reproductor */
  autoplay?: boolean;
  loop?: boolean;
  mute?: boolean;
  captions?: boolean;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoIdentifier,
  resolveFromAPI = true,
  autoplay = true,
  loop = false,
  mute = true,
  captions = false,
}) => {
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const playerOptions = useMemo<UseYouTubePlayerOptions>(
    () => ({ autoplay, loop, mute, captions }),
    [autoplay, loop, mute, captions]
  );

  const { error: playerError, restart } = useYouTubePlayer(
    'yt-player',
    youtubeVideoId,
    playerOptions
  );

  useEffect(() => {
    let cancelled = false;

    const loadVideo = async () => {
      setLoading(true);
      setFetchError(null);

      if (resolveFromAPI) {
        try {
          const entry = await fetchVideoByUUID(videoIdentifier);
          if (!cancelled) {
            setYoutubeVideoId(entry.youtubeVideoId);
          }
        } catch (err) {
          if (!cancelled) {
            setFetchError(
              err instanceof Error ? err.message : 'Error desconocido'
            );
          }
        }
      } else {
        // Usar directamente como YouTube video ID
        if (!cancelled) {
          setYoutubeVideoId(videoIdentifier);
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    loadVideo();

    return () => {
      cancelled = true;
    };
  }, [videoIdentifier, resolveFromAPI]);

  if (loading) {
    return (
      <div className="player-status">
        <div className="spinner"></div>
        <p>Cargando reproductor...</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="player-status error">
        <h1>Error</h1>
        <p>{fetchError}</p>
        <p className="video-id">ID: {videoIdentifier}</p>
      </div>
    );
  }

  return (
    <div className="video-wrapper">
      {/* Overlay bloqueante: intercepta TODA interacción con el iframe */}
      <div className="overlay"></div>
      <div id="yt-player" className="full-screen-video"></div>

      {playerError && (
        <div className="player-error-overlay">
          <h2>Error de reproducción</h2>
          <p>{playerError}</p>
          <button onClick={restart}>Reintentar</button>
        </div>
      )}
    </div>
  );
};

export default YouTubePlayer;
